import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  Box,
  Divider,
  Grid,
  CircularProgress,
  TextField,
  IconButton,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { IProduct, IGrillingServiceResponse, IGrillingConfig } from '../../common/interfaces';
import { productsService } from '../../services/productsService';
import { CommonService } from '../../services/CommonService';

interface GrillingServiceProps {
  product: IProduct;
  quantity: number;
  onConfirm: (product: IProduct, quantity: number, config: IGrillingConfig) => void;
  onCancel: () => void;
}

export const GrillingService: React.FC<GrillingServiceProps> = ({
  product,
  quantity,
  onConfirm,
  onCancel
}) => {
  const [includeGrillingService, setIncludeGrillingService] = useState(false);
  const [includeComplements, setIncludeComplements] = useState(false);
  const [complementsQuantity, setComplementsQuantity] = useState(1); // Cantidad de piezas
  const [cookingPoint, setCookingPoint] = useState<string>('TERMINO MEDIO');
  const [cutType, setCutType] = useState<string>('EN TIRAS');
  const [serviceInfo, setServiceInfo] = useState<IGrillingServiceResponse | null>(null);
  const [loading, setLoading] = useState(true);

  // Verificar si el producto es de la familia CARNES
  const isCarnesProduct = product.product_type_id === 41;

  // Si se desmarca el servicio de asado, también desmarcar complementos
  const handleGrillingServiceChange = (checked: boolean) => {
    setIncludeGrillingService(checked);
    if (!checked) {
      setIncludeComplements(false);
    }
  };

  useEffect(() => {
    loadServiceInfo();
  }, []);

  const loadServiceInfo = async () => {
    try {
      const info = await productsService.getGrillingServiceInfo(product.id, quantity);
      setServiceInfo(info);
    } catch (error) {
      console.error('Error loading grilling service info:', error);
      CommonService.toast('Error al cargar información de servicio', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getCurrentTotal = (): number => {
    if (!serviceInfo || !serviceInfo.data) return 0;

    let total = serviceInfo.data.product.subtotal;

    if (includeGrillingService && serviceInfo.data.grilling_service) {
      total += serviceInfo.data.grilling_service.subtotal;
    }

    if (includeComplements && serviceInfo.data.complements_unit_price) {
      total += serviceInfo.data.complements_unit_price * complementsQuantity;
    }

    return total;
  };

  const handleConfirm = () => {
    // Validar que si es producto CARNES y tiene servicio de asado, debe tener cocción y corte
    if (isCarnesProduct && includeGrillingService) {
      if (!cookingPoint || !cutType) {
        CommonService.toast('Debe seleccionar punto de cocción y tipo de corte', 'error');
        return;
      }
    }

    const config: IGrillingConfig = {
      has_grilling_service: includeGrillingService,
      has_complements: includeComplements,
      complements_quantity: includeComplements ? complementsQuantity : 0,
      // Solo incluir cooking_point y cut_type si es producto CARNES y tiene servicio
      ...(isCarnesProduct && includeGrillingService && {
        cooking_point: cookingPoint,
        cut_type: cutType
      })
    };
    onConfirm(product, quantity, config);
  };

  if (loading) {
    return (
      <Dialog
        open={true}
        onClose={onCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#424242',
            backgroundImage: 'none'
          }
        }}
      >
        <DialogContent sx={{ bgcolor: '#424242' }}>
          <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress sx={{ color: '#4fc3f7' }} />
            <Typography color="#ccc" mt={2}>Cargando información...</Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (!serviceInfo || !serviceInfo.data) {
    return (
      <Dialog
        open={true}
        onClose={onCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#424242',
            backgroundImage: 'none'
          }
        }}
      >
        <DialogTitle sx={{ bgcolor: '#333', borderBottom: '1px solid #555' }}>
          <Typography color="#fff" fontWeight="bold">Error</Typography>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: '#424242', mt: 2 }}>
          <Typography color="#f44336">
            Error al cargar información del servicio de asado
          </Typography>
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#333', borderTop: '1px solid #555' }}>
          <Button
            onClick={onCancel}
            variant="outlined"
            sx={{
              color: '#fff',
              borderColor: '#666',
              '&:hover': {
                borderColor: '#999',
                bgcolor: '#444'
              }
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  const { data } = serviceInfo;

  return (
    <Dialog
      open={true}
      onClose={onCancel}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#424242',
          backgroundImage: 'none',
          maxWidth: '700px'
        }
      }}
    >
      <DialogTitle sx={{ bgcolor: '#333', borderBottom: '1px solid #555', py: 1.5 }}>
        <Box display="flex" alignItems="center">
          <span style={{ fontSize: '24px', marginRight: '8px' }}>🥩</span>
          <Typography variant="h6" component="span" color="#fff" fontWeight="bold">
            Servicio de Asado - {data.product.name}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ bgcolor: '#424242', py: 2 }}>
        {/* Información del Producto */}
        <Box mb={2} p={2} bgcolor="#333" borderRadius={1} border="1px solid #555">
          <Typography variant="body2" color="#999" fontSize="0.75rem" mb={0.5}>
            Producto:
          </Typography>
          <Typography variant="h6" fontWeight="bold" color="#fff" mb={1.5}>
            {data.product.name}
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={4}>
              <Typography variant="body2" color="#999" fontSize="0.75rem">
                Cantidad:
              </Typography>
              <Typography variant="body1" fontWeight="bold" color="#fff">
                {data.product.quantity} kg
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="body2" color="#999" fontSize="0.75rem">
                Precio/kg:
              </Typography>
              <Typography variant="body1" fontWeight="bold" color="#fff">
                ${data.product.price_per_kg.toFixed(2)}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="body2" color="#999" fontSize="0.75rem">
                Subtotal:
              </Typography>
              <Typography variant="body1" fontWeight="bold" color="#4fc3f7">
                ${data.product.subtotal.toFixed(2)}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Servicios en Grid Horizontal */}
        <Grid container spacing={2} mb={2}>
          {/* Servicio de Asado */}
          {data.grilling_service && (
            <Grid item xs={12} md={6}>
              <Box p={2} bgcolor="#333" border="1px solid #555" borderRadius={1} height="100%">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeGrillingService}
                      onChange={(e) => handleGrillingServiceChange(e.target.checked)}
                      sx={{
                        color: '#999',
                        '&.Mui-checked': {
                          color: '#4fc3f7',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="subtitle1" color="#fff" fontWeight="bold">
                      🔥 Servicio de Asado
                    </Typography>
                  }
                />
                <Box ml={4} mt={1}>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="#999" fontSize="0.75rem">
                        Precio/kg:
                      </Typography>
                      <Typography variant="body1" color="#fff">
                        ${data.grilling_service.price_per_kg.toFixed(2)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="#999" fontSize="0.75rem">
                        Cantidad:
                      </Typography>
                      <Typography variant="body1" color="#fff">
                        {data.grilling_service.quantity} kg
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="#999" fontSize="0.75rem">
                        Subtotal:
                      </Typography>
                      <Typography variant="body1" fontWeight="bold" color="#4fc3f7">
                        ${data.grilling_service.subtotal.toFixed(2)}
                      </Typography>
                    </Grid>
                  </Grid>

                  {/* Opciones de Cocción y Corte - Solo para productos CARNES */}
                  {isCarnesProduct && includeGrillingService && (
                    <Box mt={2} pt={2} borderTop="1px solid #555">
                      {/* Punto de Cocción */}
                      <FormControl component="fieldset" fullWidth sx={{ mb: 2 }}>
                        <FormLabel component="legend" sx={{ color: '#4fc3f7', fontSize: '0.875rem', mb: 1 }}>
                          Punto de cocción:
                        </FormLabel>
                        <RadioGroup
                          value={cookingPoint}
                          onChange={(e) => setCookingPoint(e.target.value)}
                        >
                          <FormControlLabel
                            value="TERMINO MEDIO"
                            control={<Radio sx={{ color: '#999', '&.Mui-checked': { color: '#4fc3f7' } }} />}
                            label={<Typography variant="body2" color="#fff">Término Medio</Typography>}
                          />
                          <FormControlLabel
                            value="3/4"
                            control={<Radio sx={{ color: '#999', '&.Mui-checked': { color: '#4fc3f7' } }} />}
                            label={<Typography variant="body2" color="#fff">3/4</Typography>}
                          />
                          <FormControlLabel
                            value="BIEN COCIDO"
                            control={<Radio sx={{ color: '#999', '&.Mui-checked': { color: '#4fc3f7' } }} />}
                            label={<Typography variant="body2" color="#fff">Bien Cocido</Typography>}
                          />
                        </RadioGroup>
                      </FormControl>

                      {/* Tipo de Corte */}
                      <FormControl component="fieldset" fullWidth>
                        <FormLabel component="legend" sx={{ color: '#4fc3f7', fontSize: '0.875rem', mb: 1 }}>
                          Tipo de corte:
                        </FormLabel>
                        <RadioGroup
                          value={cutType}
                          onChange={(e) => setCutType(e.target.value)}
                        >
                          <FormControlLabel
                            value="EN TIRAS"
                            control={<Radio sx={{ color: '#999', '&.Mui-checked': { color: '#4fc3f7' } }} />}
                            label={<Typography variant="body2" color="#fff">En Tiras</Typography>}
                          />
                          <FormControlLabel
                            value="ENTERA"
                            control={<Radio sx={{ color: '#999', '&.Mui-checked': { color: '#4fc3f7' } }} />}
                            label={<Typography variant="body2" color="#fff">Entera</Typography>}
                          />
                          <FormControlLabel
                            value="PICADA PARA TACO"
                            control={<Radio sx={{ color: '#999', '&.Mui-checked': { color: '#4fc3f7' } }} />}
                            label={<Typography variant="body2" color="#fff">Picada para Taco</Typography>}
                          />
                        </RadioGroup>
                      </FormControl>
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>
          )}

          {/* Complementos */}
          {data.complements && (
            <Grid item xs={12} md={6}>
              <Box p={2} bgcolor="#333" border="1px solid #555" borderRadius={1} height="100%">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeComplements}
                      onChange={(e) => setIncludeComplements(e.target.checked)}
                      disabled={!includeGrillingService}
                      sx={{
                        color: '#999',
                        '&.Mui-checked': {
                          color: '#4fc3f7',
                        },
                        '&.Mui-disabled': {
                          color: '#555',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography
                      variant="subtitle1"
                      color={includeGrillingService ? "#fff" : "#666"}
                      fontWeight="bold"
                    >
                      🍽️ Complementos de Servicio
                    </Typography>
                  }
                />
                <Box ml={4} mt={1}>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={12}>
                      <Typography variant="body2" color="#999" fontSize="0.75rem">
                        Precio por pieza:
                      </Typography>
                      <Typography variant="body1" color="#fff">
                        ${data.complements_unit_price?.toFixed(2) ?? '0.00'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="#999" fontSize="0.75rem" mb={0.5}>
                        Cantidad:
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1}>
                        <IconButton
                          size="small"
                          onClick={() => setComplementsQuantity(Math.max(1, complementsQuantity - 1))}
                          disabled={!includeComplements || complementsQuantity <= 1}
                          sx={{
                            color: '#4fc3f7',
                            '&.Mui-disabled': { color: '#555' }
                          }}
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <TextField
                          type="number"
                          value={complementsQuantity}
                          onChange={(e) => {
                            const val = parseInt(e.target.value);
                            if (!isNaN(val) && val >= 1) {
                              setComplementsQuantity(val);
                            }
                          }}
                          disabled={!includeComplements}
                          inputProps={{ min: 1, style: { textAlign: 'center' } }}
                          sx={{
                            width: '60px',
                            '& .MuiInputBase-input': {
                              color: '#fff',
                              padding: '4px 8px'
                            },
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': { borderColor: '#555' },
                              '&:hover fieldset': { borderColor: '#999' },
                              '&.Mui-focused fieldset': { borderColor: '#4fc3f7' }
                            }
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => setComplementsQuantity(complementsQuantity + 1)}
                          disabled={!includeComplements}
                          sx={{
                            color: '#4fc3f7',
                            '&.Mui-disabled': { color: '#555' }
                          }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="#999" fontSize="0.75rem">
                        Subtotal:
                      </Typography>
                      <Typography variant="body1" fontWeight="bold" color="#4fc3f7">
                        ${data.complements_unit_price
                          ? (data.complements_unit_price * complementsQuantity).toFixed(2)
                          : '0.00'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>

        {/* Resumen Total - Compacto */}
        <Box p={2} bgcolor="#2a2a2a" borderRadius={1} border="2px solid #555">
          <Typography variant="subtitle1" gutterBottom color="#fff" fontWeight="bold" mb={1}>
            RESUMEN:
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={8}>
              <Typography color="#ccc" fontSize="0.9rem">
                Producto ({data.product.quantity} kg)
              </Typography>
            </Grid>
            <Grid item xs={4} textAlign="right">
              <Typography color="#fff" fontWeight="medium" fontSize="0.9rem">
                ${data.product.subtotal.toFixed(2)}
              </Typography>
            </Grid>

            <Grid item xs={8}>
              <Typography color="#ccc" fontSize="0.9rem">Servicio de Asado</Typography>
            </Grid>
            <Grid item xs={4} textAlign="right">
              <Typography color="#fff" fontWeight="medium" fontSize="0.9rem">
                ${includeGrillingService && data.grilling_service
                  ? data.grilling_service.subtotal.toFixed(2)
                  : '0.00'}
              </Typography>
            </Grid>

            <Grid item xs={8}>
              <Typography color="#ccc" fontSize="0.9rem">
                Complementos {includeComplements ? `(${complementsQuantity} pzas)` : ''}
              </Typography>
            </Grid>
            <Grid item xs={4} textAlign="right">
              <Typography color="#fff" fontWeight="medium" fontSize="0.9rem">
                ${includeComplements && data.complements_unit_price
                  ? (data.complements_unit_price * complementsQuantity).toFixed(2)
                  : '0.00'}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 0.5, borderColor: '#666' }} />
            </Grid>

            <Grid item xs={8}>
              <Typography variant="h6" fontWeight="bold" color="#fff">
                TOTAL:
              </Typography>
            </Grid>
            <Grid item xs={4} textAlign="right">
              <Typography variant="h6" fontWeight="bold" color="#4fc3f7">
                ${getCurrentTotal().toFixed(2)}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ bgcolor: '#333', borderTop: '1px solid #555', px: 3, py: 1.5 }}>
        <Button
          onClick={onCancel}
          variant="outlined"
          sx={{
            color: '#fff',
            borderColor: '#666',
            '&:hover': {
              borderColor: '#999',
              bgcolor: '#444'
            }
          }}
        >
          CANCELAR
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          sx={{
            bgcolor: '#4fc3f7',
            color: '#000',
            fontWeight: 'bold',
            '&:hover': {
              bgcolor: '#29b6f6'
            }
          }}
        >
          AGREGAR AL CARRITO
        </Button>
      </DialogActions>
    </Dialog>
  );
};

